/**
 * 計算フィールド 計算エンジン（#3145）の型変換（設計書 §4.3）。
 * ⚠️ functions/src/utils/calculation と main/src/utils/calculation は同一内容。
 *    片方だけ変更しないこと（変更時は両方へ反映し、共通テストで一致を確認する）。
 */
import { CALCULATION_RESULT_SIGNIFICANT_DIGITS, calculationErrorCode } from './constants';
import { CalculationFormulaError } from './calculationError';
import { EvaluatedValue } from './types';

/** カンマ除去後に数値として解釈できる形式（半角数字・小数・符号のみ） */
const NUMERIC_PATTERN = /^[+-]?[0-9]+(\.[0-9]+)?$/;

/**
 * 数値を文字列化する。桁区切りは付けず、浮動小数点誤差は有効桁数で丸める
 * （例: 0.1 + 0.2 → "0.3"）
 */
export const formatNumericValue = (value: number): string => {
  return String(parseFloat(value.toPrecision(CALCULATION_RESULT_SIGNIFICANT_DIGITS)));
};

/**
 * 数値文脈への変換。空値（null・空文字）は null を返して空値伝播させる。
 * カンマは除去して変換し（"1,000" → 1000）、変換できなければ not_number エラーを投げる
 */
export const toNumericValue = (value: EvaluatedValue): number | null => {
  if (value === null) {
    return null;
  }
  if (typeof value === 'number') {
    return value;
  }
  const normalized = value.trim().replace(/,/g, '');
  if (normalized === '') {
    return null;
  }
  if (!NUMERIC_PATTERN.test(normalized)) {
    throw new CalculationFormulaError(calculationErrorCode.notNumber, `値: ${value}`);
  }
  return Number(normalized);
};

/** 文字列文脈への変換。空値は空文字、数値は桁区切りなしの文字列にする */
export const toTextValue = (value: EvaluatedValue): string => {
  if (value === null) {
    return '';
  }
  if (typeof value === 'number') {
    return formatNumericValue(value);
  }
  return value;
};
